/**
 * Accessibility Guide Page (Admin Only)
 *
 * Comprehensive WCAG AA 2.2 accessibility standards for MoneyMojo.
 * Internal reference. Not customer-facing.
 */

import 'remixicon/fonts/remixicon.css';
import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { PageHeader } from '../../components/PageHeader';

type Section =
  | 'overview'
  | 'principles'
  | 'color-contrast'
  | 'keyboard'
  | 'screen-reader'
  | 'forms'
  | 'mobile'
  | 'financial'
  | 'checklist';

const SECTIONS: { id: Section; label: string; iconClass: string }[] = [
  { id: 'overview', label: 'Overview', iconClass: 'ri-shield-check-line' },
  { id: 'principles', label: 'WCAG 2.2 Principles', iconClass: 'ri-file-list-3-line' },
  { id: 'color-contrast', label: 'Color Contrast', iconClass: 'ri-palette-line' },
  { id: 'keyboard', label: 'Keyboard Navigation', iconClass: 'ri-keyboard-line' },
  { id: 'screen-reader', label: 'Screen Reader', iconClass: 'ri-eye-line' },
  { id: 'forms', label: 'Form Accessibility', iconClass: 'ri-edit-2-line' },
  { id: 'mobile', label: 'Mobile & Touch', iconClass: 'ri-smartphone-line' },
  { id: 'financial', label: 'Financial App Specific', iconClass: 'ri-bank-card-line' },
  { id: 'checklist', label: 'Testing Checklist', iconClass: 'ri-checkbox-multiple-line' },
];

const CHECKLIST_CATEGORIES: { title: string; items: string[] }[] = [
  {
    title: 'Visual Design & Readability',
    items: [
      'Colour contrast ratio (AA/AAA) checked across all elements',
      'Text size is adjustable and responsive',
      'Clear and consistent typography hierarchy',
      'Hover states provide sufficient contrast',
      'Focus indicators are visible and distinct',
      'Icons have descriptive labels or alt text',
    ],
  },
  {
    title: 'Keyboard Navigation',
    items: [
      'All interactive elements are keyboard accessible',
      'Tab order is logical and predictable',
      'Skip links are present for repeated content',
      'No keyboard traps exist',
    ],
  },
  {
    title: 'Screen Reader Compatibility',
    items: [
      'Semantic HTML5 elements used correctly',
      'Meaningful alt text provided for all images',
      'ARIA attributes used appropriately for dynamic content',
      'Form controls have explicit labels',
      'Live regions are managed for dynamic updates',
    ],
  },
  {
    title: 'Forms & Input',
    items: [
      'Form fields have clear, visible labels',
      'Error messages are descriptive and actionable',
      'Required fields are clearly indicated',
      'Input validation provides helpful feedback',
      'Autofill attributes are correctly used',
    ],
  },
  {
    title: 'Multimedia',
    items: [
      'Videos have captions and/or transcripts',
      'Audio content has transcripts',
      'Media players are keyboard accessible',
      'Auto-playing media can be paused or disabled',
    ],
  },
  {
    title: 'Error Handling & Feedback',
    items: [
      'Error messages are clearly associated with fields',
      'Success and error states are announced',
      'Loading states are communicated',
      'Confirmation dialogs are accessible',
    ],
  },
  {
    title: 'Responsiveness & Mobile',
    items: [
      'Layout adapts to viewport size',
      'Touch targets meet minimum 44×44px',
      'Content remains usable at 200% zoom',
      'Orientation changes do not break functionality',
    ],
  },
];

function exportAccessibilityChecklistToPdf(): void {
  const categoriesHtml = CHECKLIST_CATEGORIES.map(
    (cat) => `
    <div style="margin-bottom: 1.5rem; break-inside: avoid;">
      <h3 style="font-size: 1rem; font-weight: 600; margin: 0 0 0.5rem 0; color: #111827;">${escapeHtml(cat.title)}</h3>
      <ul style="margin: 0; padding-left: 1.25rem; list-style-type: disc;">
        ${cat.items.map((item) => `<li style="margin-bottom: 0.25rem; color: #374151;">${escapeHtml(item)}</li>`).join('')}
      </ul>
    </div>`
  ).join('');

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Accessibility Testing Checklist - MoneyMojo</title>
  <style>
    body { font-family: system-ui, sans-serif; max-width: 800px; margin: 2rem auto; padding: 0 1.5rem; color: #111827; }
    h1 { font-size: 1.5rem; margin: 0 0 0.25rem 0; }
    .subtitle { color: #6b7280; font-size: 0.875rem; margin-bottom: 1.5rem; }
    @media print { body { margin: 1rem; } }
  </style>
</head>
<body>
  <h1>Accessibility Testing Checklist</h1>
  <p class="subtitle">Ensure your application meets accessibility standards before deployment. MoneyMojo – WCAG 2.2 Level AA.</p>
  ${categoriesHtml}
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) {
    alert('Popup blocked. Please allow popups for this site to export the checklist.');
    return;
  }
  win.document.write(html);
  win.document.close();
  win.focus();
  win.onafterprint = () => win.close();
  setTimeout(() => win.print(), 100);
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

export function AccessibilityGuidePage(): React.ReactElement {
  const [activeSection, setActiveSection] = useState<Section>('overview');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const activeSectionData = SECTIONS.find((s) => s.id === activeSection) ?? SECTIONS[0];

  const handleSectionSelect = (section: Section) => {
    setActiveSection(section);
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    if (isDropdownOpen) {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setIsDropdownOpen(false);
      };
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isDropdownOpen]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Accessibility Guidelines"
        description="Comprehensive WCAG AA 2.2 accessibility standards for inclusive financial management."
        headingLevel="h1"
      />

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar */}
        <aside className="w-full lg:w-64 flex-shrink-0">
          <nav
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4"
            aria-label="Accessibility sections"
          >
            <div className="hidden lg:block space-y-1">
              {SECTIONS.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    activeSection === section.id
                      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                  aria-current={activeSection === section.id ? 'page' : undefined}
                >
                  <i className={`${section.iconClass} ri-lg`} aria-hidden />
                  {section.label}
                </button>
              ))}
            </div>

            <div className="lg:hidden relative">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                aria-label="Select accessibility section"
                aria-expanded={isDropdownOpen}
                aria-haspopup="listbox"
              >
                <div className="flex items-center gap-3">
                  <i className={`${activeSectionData.iconClass} ri-lg`} aria-hidden />
                  <span className="text-sm font-medium">{activeSectionData.label}</span>
                </div>
                <ChevronDown
                  className={`w-5 h-5 flex-shrink-0 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>

              {isDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setIsDropdownOpen(false)} aria-hidden />
                  <ul
                    className="absolute z-20 w-full mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                    role="listbox"
                  >
                    {SECTIONS.map((section) => (
                      <li key={section.id} role="option" aria-selected={activeSection === section.id}>
                        <button
                          type="button"
                          onClick={() => handleSectionSelect(section.id)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                            activeSection === section.id
                              ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400 font-semibold'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          }`}
                        >
                          <i className={`${section.iconClass} ri-lg`} aria-hidden />
                          <span className="text-sm font-medium">{section.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0 space-y-10" id="main-content">
          {activeSection === 'overview' && <OverviewSection />}
          {activeSection === 'principles' && <PrinciplesSection />}
          {activeSection === 'color-contrast' && <ColorContrastSection />}
          {activeSection === 'keyboard' && <KeyboardSection />}
          {activeSection === 'screen-reader' && <ScreenReaderSection />}
          {activeSection === 'forms' && <FormsSection />}
          {activeSection === 'mobile' && <MobileSection />}
          {activeSection === 'financial' && <FinancialSection />}
          {activeSection === 'checklist' && <ChecklistSection />}
        </main>
      </div>
    </div>
  );
}

function OverviewSection(): React.ReactElement {
  return (
    <section aria-labelledby="overview-heading">
      <h2 id="overview-heading" className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Accessibility Overview
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-8">
        Our accessibility standards ensure Money Mojo is usable by everyone, meeting WCAG AA 2.2 compliance for
        inclusive financial management.
      </p>

      <div className="grid gap-6 lg:grid-cols-2 mb-10">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Why Accessibility Matters</h3>
          <ul className="space-y-3">
            {[
              { icon: 'ri-user-line', title: 'Inclusive Design', desc: 'Makes financial tools accessible to users with disabilities' },
              { icon: 'ri-check-line', title: 'Legal Compliance', desc: 'Meets ADA and international accessibility standards' },
              { icon: 'ri-heart-line', title: 'Better UX', desc: 'Improves usability for all users, not just those with disabilities' },
              { icon: 'ri-global-line', title: 'Wider Reach', desc: 'Expands potential user base to underserved communities' },
            ].map(({ icon, title, desc }) => (
              <li key={title} className="flex gap-3">
                <i className={`${icon} ri-lg text-primary-600 flex-shrink-0 mt-0.5`} aria-hidden />
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{title}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Key Statistics</h3>
          <div className="space-y-4">
            {[
              { stat: '1 in 5', label: 'Australians have disability', color: 'primary' },
              { stat: '71%', label: 'Leave websites that aren\'t accessible', color: 'success' },
              { stat: 'AU$54B', label: 'Annual disposable income of people with disability (Australia)', color: 'secondary' },
            ].map(({ stat, label, color }) => (
              <div
                key={stat}
                className={`rounded-xl p-4 ${
                  color === 'primary'
                    ? 'bg-primary-50 dark:bg-primary-900/20'
                    : color === 'success'
                      ? 'bg-success-50 dark:bg-success-900/20'
                      : 'bg-secondary-50 dark:bg-secondary-900/20'
                }`}
              >
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stat}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl bg-gradient-to-r from-primary-600 to-secondary-600 p-6">
        <h3 className="text-lg font-medium text-white mb-4">Our Accessibility Commitment</h3>
        <div className="grid gap-6 sm:grid-cols-3">
          <div className="rounded-lg bg-white/10 p-4 backdrop-blur-sm">
            <i className="ri-shield-check-line ri-2x text-white mb-2 block" aria-hidden />
            <p className="font-medium text-white">WCAG 2.2 AA</p>
            <p className="mt-1 text-sm text-white/90">
              Full compliance with the latest web accessibility guidelines for comprehensive coverage.
            </p>
          </div>
          <div className="rounded-lg bg-white/10 p-4 backdrop-blur-sm">
            <i className="ri-user-settings-line ri-2x text-white mb-2 block" aria-hidden />
            <p className="font-medium text-white">User-Centred</p>
            <p className="mt-1 text-sm text-white/90">
              Regular testing with users who have disabilities to ensure real-world usability.
            </p>
          </div>
          <div className="rounded-lg bg-white/10 p-4 backdrop-blur-sm">
            <i className="ri-refresh-line ri-2x text-white mb-2 block" aria-hidden />
            <p className="font-medium text-white">Continuous Improvement</p>
            <p className="mt-1 text-sm text-white/90">
              Regular audits and updates to maintain the highest accessibility standards.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PrinciplesSection(): React.ReactElement {
  const principles = [
    {
      title: 'Perceivable',
      icon: 'ri-focus-3-line',
      color: 'primary',
      desc: 'Information must be presentable in ways users can perceive',
      items: [
        'Provide text alternatives for images',
        'Offer captions and alternatives for multimedia',
        'Ensure sufficient colour contrast',
        'Make content adaptable to different presentations',
      ],
    },
    {
      title: 'Operable',
      icon: 'ri-hand-heart-line',
      color: 'success',
      desc: 'Interface components must be operable by all users',
      items: [
        'Make all functionality keyboard accessible',
        'Give users enough time to read content',
        'Don\'t use content that causes seizures',
        'Help users navigate and find content',
      ],
    },
    {
      title: 'Understandable',
      icon: 'ri-information-line',
      color: 'secondary',
      desc: 'Information and UI operation must be understandable',
      items: [
        'Make text readable and understandable',
        'Make content appear and operate predictably',
        'Help users avoid and correct mistakes',
        'Use clear and simple language',
      ],
    },
    {
      title: 'Robust',
      icon: 'ri-shield-star-line',
      color: 'warning',
      desc: 'Content must be robust enough for assistive technologies',
      items: [
        'Maximise compatibility with assistive tools',
        'Use valid, semantic HTML markup',
        'Ensure content works across browsers',
        'Future-proof against technology changes',
      ],
    },
  ];

  return (
    <section aria-labelledby="principles-heading">
      <h2 id="principles-heading" className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
        WCAG 2.2 Principles
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-8">
        The four foundational principles that guide all accessibility decisions in Money Mojo.
      </p>
      <div className="grid gap-6 sm:grid-cols-2">
        {principles.map(({ title, icon, desc, items }) => (
          <div
            key={title}
            className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800"
          >
            <i className={`${icon} ri-2x text-primary-600 mb-3 block`} aria-hidden />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{desc}</p>
            <ul className="mt-4 list-inside list-disc space-y-1 text-sm text-gray-700 dark:text-gray-300">
              {items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function ColorContrastSection(): React.ReactElement {
  return (
    <section aria-labelledby="color-contrast-heading">
      <h2 id="color-contrast-heading" className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
        Color Contrast Standards
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-8">
        WCAG AA contrast requirements for text and UI components.
      </p>

      <div className="space-y-8">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">WCAG AA Requirements</h3>
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              { label: 'Normal Text', desc: 'Minimum contrast ratio for regular text (under 18pt)', ratio: '4.5:1' },
              { label: 'Large Text', desc: 'Minimum contrast for large text (18pt+ or 14pt+ bold)', ratio: '3:1' },
              { label: 'UI Components', desc: 'Graphical objects, form controls, focus indicators', ratio: '3:1' },
            ].map(({ label, desc, ratio }) => (
              <div key={label} className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                <p className="font-medium text-gray-900 dark:text-gray-100">{label}</p>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{desc}</p>
                <span className="mt-2 inline-block rounded-full bg-success-100 px-2.5 py-0.5 text-xs font-medium text-success-800 dark:bg-success-900/30 dark:text-success-300">
                  {ratio}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Approved Colour Combinations</h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { bg: 'bg-primary-600', text: 'text-white', label: 'Primary Blue on White', ratio: '7.2:1' },
              { bg: 'bg-gray-900', text: 'text-white', label: 'Gray 900 on White', ratio: '16.7:1' },
              { bg: 'bg-gray-600', text: 'text-white', label: 'Gray 600 on White', ratio: '5.7:1' },
              { bg: 'bg-success-600', text: 'text-white', label: 'Success Green on White', ratio: '4.8:1' },
            ].map(({ bg, text, label, ratio }) => (
              <div key={label} className={`rounded-lg p-4 ${bg} ${text}`}>
                <p className="font-medium">Sample Text</p>
                <p className="mt-1 text-xs opacity-90">{label}</p>
                <p className="mt-2 text-xs font-mono">{ratio}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Accessible Examples</h3>
          <div className="flex flex-wrap gap-4">
            <div className="rounded-lg border border-gray-200 bg-primary-600 px-4 py-2 text-white">
              <p className="font-medium">Primary Button Text</p>
              <p className="text-xs opacity-90">High contrast ensures readability</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
              <p className="font-medium text-gray-900 dark:text-gray-100">Body Text</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Secondary information with sufficient contrast</p>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Avoid These</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-gray-100 p-4 dark:bg-gray-700">
              <p className="text-gray-400 dark:text-gray-500">Low Contrast Text</p>
              <p className="text-xs text-gray-500 dark:text-gray-600">Fails WCAG requirements</p>
            </div>
            <div className="rounded-lg border border-yellow-200 bg-yellow-100 p-4 dark:bg-yellow-900/20">
              <p className="text-yellow-600 dark:text-yellow-400">Poor Colour Choice</p>
              <p className="text-xs text-yellow-500 dark:text-yellow-600">Insufficient contrast ratio</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function KeyboardSection(): React.ReactElement {
  return (
    <section aria-labelledby="keyboard-heading">
      <h2 id="keyboard-heading" className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
        Keyboard Navigation
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-8">
        All interactive elements must be operable via keyboard.
      </p>

      <div className="space-y-8">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Required Keyboard Support</h3>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-800">
                  <th className="px-4 py-3 text-left font-medium">Key</th>
                  <th className="px-4 py-3 text-left font-medium">Action</th>
                  <th className="px-4 py-3 text-left font-medium">Description</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Tab', 'Navigate Forward', 'Move to next focusable element'],
                  ['Shift + Tab', 'Navigate Backward', 'Move to previous focusable element'],
                  ['Enter/Space', 'Activate', 'Trigger buttons and links'],
                  ['Esc', 'Cancel/Close', 'Close modals and dropdowns'],
                  ['Arrow Keys', 'Navigate Lists', 'Move within menus and lists'],
                ].map(([key, action, desc]) => (
                  <tr key={key} className="border-b border-gray-200 last:border-0 dark:border-gray-600">
                    <td className="px-4 py-3 font-mono text-primary-600 dark:text-primary-400">{key}</td>
                    <td className="px-4 py-3 font-medium">{action}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Focus Management</h3>
          <ul className="space-y-4">
            <li>
              <p className="font-medium text-gray-900 dark:text-gray-100">Visible Focus Indicator</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                All interactive elements must have clear focus states.
              </p>
              <button
                type="button"
                className="mt-2 rounded-md border-2 border-primary-600 bg-primary-600 px-4 py-2 text-sm font-medium text-white ring-2 ring-primary-600 ring-offset-2"
              >
                Example Button
              </button>
            </li>
            <li>
              <p className="font-medium text-gray-900 dark:text-gray-100">Logical Tab Order</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Focus moves in reading order (left-to-right, top-to-bottom).
              </p>
            </li>
            <li>
              <p className="font-medium text-gray-900 dark:text-gray-100">Focus Trapping</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Modal dialogs trap focus within the modal content.
              </p>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Implementation Example</h3>
          <pre className="overflow-x-auto rounded-lg bg-gray-900 p-4 text-sm text-gray-100">
            <code>{`// Focus management for buttons
className="focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2"

// Skip links for screen readers
<a href="#main-content"
  className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-primary-600 text-white px-4 py-2 rounded">
  Skip to main content
</a>`}</code>
          </pre>
        </div>
      </div>
    </section>
  );
}

function ScreenReaderSection(): React.ReactElement {
  return (
    <section aria-labelledby="screen-reader-heading">
      <h2 id="screen-reader-heading" className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
        Screen Reader
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-8">
        Optimising content for screen readers. Understand how users interact with assistive technologies.
      </p>

      <div className="space-y-8">
        {/* Focus Order */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Focus Order (DOM order)</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Screen readers generally follow the Document Object Model (DOM) order for navigation.
          </p>
          <div className="flex flex-wrap gap-4">
            {['Card A', 'Card B', 'Card C'].map((label, i) => (
              <div
                key={label}
                className="flex flex-1 min-w-[120px] flex-col rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-700/50"
              >
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-success-600 text-xs font-bold text-white">
                  {i + 1}
                </span>
                <p className="mt-2 font-medium">{label}</p>
                <p className="text-sm text-primary-600 underline">This is the {['first', 'second', 'third'][i]} card</p>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-success-50 p-3 dark:bg-success-900/20">
            <i className="ri-check-line ri-lg text-success-600" aria-hidden />
            <p className="text-sm text-success-700 dark:text-success-300">
              The focus order follows the visual order of the cards, from left to right.
            </p>
          </div>
        </div>

        {/* Labels */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Labels (aria-label)</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Provide clear and descriptive labels for interactive elements using aria-label.
          </p>
          <div className="flex flex-wrap gap-3">
            <input type="search" placeholder="Search" className="input max-w-[200px]" aria-label="Search" />
            <button type="button" className="btn btn-primary btn-sm">
              <i className="ri-search-line" aria-hidden /> Search
            </button>
            <button type="button" className="btn btn-ghost btn-sm">
              <i className="ri-close-line" aria-hidden /> Dismiss
            </button>
            <button type="button" className="btn btn-ghost btn-sm">
              <i className="ri-settings-3-line" aria-hidden /> Settings
            </button>
          </div>
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-success-50 p-3 dark:bg-success-900/20">
            <i className="ri-check-line ri-lg text-success-600" aria-hidden />
            <p className="text-sm text-success-700 dark:text-success-300">
              All interactive elements have clear and descriptive labels for screen readers.
            </p>
          </div>
        </div>

        {/* Landmarks */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Landmarks (ARIA roles)</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Use ARIA roles to define page landmarks for easier navigation by screen reader users.
          </p>
          <div className="grid grid-cols-3 gap-2 text-center text-sm">
            <div className="col-span-3 rounded border border-gray-200 bg-gray-50 p-2 dark:border-gray-600 dark:bg-gray-700/50">
              Header
            </div>
            <div className="rounded border border-gray-200 bg-gray-50 p-2 dark:border-gray-600 dark:bg-gray-700/50">
              Navigation
            </div>
            <div className="rounded border border-primary-200 bg-primary-50 p-2 dark:border-primary-800 dark:bg-primary-900/20">
              Main content
            </div>
            <div className="rounded border border-gray-200 bg-gray-50 p-2 dark:border-gray-600 dark:bg-gray-700/50">
              Aside / Supplementary
            </div>
            <div className="col-span-3 rounded border border-gray-200 bg-gray-50 p-2 dark:border-gray-600 dark:bg-gray-700/50">
              Footer
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-success-50 p-3 dark:bg-success-900/20">
            <i className="ri-check-line ri-lg text-success-600" aria-hidden />
            <p className="text-sm text-success-700 dark:text-success-300">
              The page uses appropriate ARIA roles to define clear landmarks for screen reader navigation.
            </p>
          </div>
        </div>

        {/* Screen Reader Experience */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Screen Reader Experience</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Learn how screen readers navigate and interpret your content. Ensure all interactive elements are properly
            announced.
          </p>
          <div className="rounded-lg bg-gray-100 p-4 font-mono text-xs dark:bg-gray-700">
            <p>Region: Main</p>
            <p>Heading level 1: Welcome to Money Mojo</p>
            <p>Link: Skip to main content</p>
            <p>Navigation: Primary menu</p>
            <p>Link: Dashboard</p>
            <p>Link: Accounts (Current page)</p>
            <p>Link: Transactions</p>
            <p>Button: Add new expense</p>
            <p>Image: Logo for Money Mojo, alt text &quot;Money Mojo logo&quot;</p>
            <p>Form field: Search, with placeholder &quot;Search transactions&quot;</p>
            <p>List with 5 items</p>
            <p>List item 1: Transaction from &quot;Starbucks&quot;, amount &quot;$5.50&quot;, category &quot;Food&quot;</p>
            <p>List item 2: Transaction from &quot;Amazon&quot;, amount &quot;$45.00&quot;, category &quot;Shopping&quot;</p>
          </div>
        </div>

        {/* Checklist */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Screen Reader Checklist</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            A quick guide to common screen reader considerations.
          </p>
          <ul className="space-y-2">
            {[
              'Use semantic HTML (headings, lists, buttons, links)',
              'Provide meaningful alt text for images',
              'Ensure form labels are associated with their inputs',
              'Define ARIA roles and attributes where native HTML is insufficient',
              'Test with common screen readers (e.g. JAWS, NVDA, VoiceOver)',
              'Avoid using CSS for essential content',
              'Provide clear focus indicators',
              'Ensure dynamic content updates are announced',
              'Offer &quot;skip to content&quot; links',
            ].map((item) => (
              <li key={item} className="flex items-center gap-2">
                <i className="ri-checkbox-blank-line ri-lg text-gray-400" aria-hidden />
                <span className="text-gray-700 dark:text-gray-300">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Code Example */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Code Example: Semantic Button</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Correctly implement a button for screen readers. Hide decorative icons from announcements while providing a
            clear label.
          </p>
          <pre className="overflow-x-auto rounded-lg bg-gray-900 p-4 text-sm text-gray-100">
            <code>{`<button type="button">
  <span aria-hidden="true">
    <i className="ri-add-line"></i>
  </span>
  <span>Add New Item</span>
</button>`}</code>
          </pre>
        </div>

        {/* Tips for Testing */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Tips for Testing</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Practical advice for evaluating your site with screen readers.
          </p>
          <ul className="list-inside list-disc space-y-1 text-sm text-gray-700 dark:text-gray-300">
            <li>Use a screen reader yourself to gain empathy and understand the user experience</li>
            <li>Navigate using only the keyboard to verify logical focus order</li>
            <li>Test with different screen readers (NVDA on Windows, VoiceOver on Mac/iOS)</li>
            <li>Verify that all interactive elements are announced with meaningful names</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function FormsSection(): React.ReactElement {
  return (
    <section aria-labelledby="forms-heading">
      <h2 id="forms-heading" className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
        Form Accessibility
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-8">
        Create accessible forms with proper labels, semantics, and error handling.
      </p>

      <div className="space-y-8">
        {/* Semantic HTML Example */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Semantic HTML</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Use native form elements and associate labels with inputs for built-in accessibility.
          </p>
          <pre className="overflow-x-auto rounded-lg bg-gray-900 p-4 text-sm text-gray-100">
            <code>{`<label for="email">Email address</label>
<input id="email" type="email" required />
<label for="password">Password</label>
<input id="password" type="password" required />
<button type="submit">Log in</button>`}</code>
          </pre>
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-success-50 p-3 dark:bg-success-900/20">
            <i className="ri-check-line ri-lg text-success-600" aria-hidden />
            <p className="text-sm text-success-700 dark:text-success-300">
              Labels are properly associated with inputs via for/id. Required fields are indicated.
            </p>
          </div>
        </div>

        {/* ARIA Example */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">ARIA when Semantic HTML is not enough</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Use ARIA attributes when custom components cannot use native form elements.
          </p>
          <pre className="overflow-x-auto rounded-lg bg-gray-900 p-4 text-sm text-gray-100">
            <code>{`<div role="group" aria-labelledby="amount-label">
  <span id="amount-label">Amount</span>
  <input type="text" aria-required="true" aria-invalid="false"
         aria-describedby="amount-error" />
  <span id="amount-error" role="alert">Please enter a valid amount.</span>
</div>`}</code>
          </pre>
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-success-50 p-3 dark:bg-success-900/20">
            <i className="ri-check-line ri-lg text-success-600" aria-hidden />
            <p className="text-sm text-success-700 dark:text-success-300">
              ARIA roles, labels, and live regions provide structure and announcements for custom controls.
            </p>
          </div>
        </div>

        {/* Critical / Major / Good Practices */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Form Accessibility Levels</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Prioritise these practices when building forms.
          </p>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-lg border-2 border-error-500 bg-error-50 p-4 dark:border-error-600 dark:bg-error-900/20">
              <h4 className="font-semibold text-error-700 dark:text-error-300">Critical</h4>
              <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-error-700 dark:text-error-300">
                <li>Labels for all inputs</li>
                <li>Error messages linked to fields</li>
                <li>Required fields indicated</li>
              </ul>
            </div>
            <div className="rounded-lg border-2 border-warning-500 bg-warning-50 p-4 dark:border-warning-600 dark:bg-warning-900/20">
              <h4 className="font-semibold text-warning-700 dark:text-warning-300">Major</h4>
              <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-warning-700 dark:text-warning-300">
                <li>Logical field groupings</li>
                <li>Clear validation feedback</li>
                <li>Sufficient focus order</li>
              </ul>
            </div>
            <div className="rounded-lg border-2 border-success-500 bg-success-50 p-4 dark:border-success-600 dark:bg-success-900/20">
              <h4 className="font-semibold text-success-700 dark:text-success-300">Good</h4>
              <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-success-700 dark:text-success-300">
                <li>Helpful placeholder text</li>
                <li>Autocomplete attributes</li>
                <li>Inline help text</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MobileSection(): React.ReactElement {
  return (
    <section aria-labelledby="mobile-heading">
      <h2 id="mobile-heading" className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
        Mobile & Touch
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-8">
        Touch targets, responsive design, and mobile-specific accessibility.
      </p>

      <div className="space-y-8">
        {/* Device settings controls */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Device Accessibility Settings</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Consider user device settings that affect accessibility. Where possible, respect or offer similar controls.
          </p>
          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap">
            <label className="flex items-center gap-3">
              <input type="checkbox" defaultChecked className="h-4 w-4 rounded border-gray-300" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Screen Reader support</span>
            </label>
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Font size</span>
              </label>
              <div className="flex gap-2">
                <button type="button" className="btn btn-ghost btn-sm">Small</button>
                <button type="button" className="btn btn-primary btn-sm">Medium</button>
                <button type="button" className="btn btn-ghost btn-sm">Large</button>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Contrast</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" className="h-4 w-4 rounded border-gray-300" />
                <span className="text-sm text-gray-700 dark:text-gray-300">High contrast mode</span>
              </label>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-success-50 p-3 dark:bg-success-900/20">
            <i className="ri-check-line ri-lg text-success-600" aria-hidden />
            <p className="text-sm text-success-700 dark:text-success-300">
              Support system-level accessibility settings and avoid overriding user preferences.
            </p>
          </div>
        </div>

        {/* Touch & responsiveness */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Touch Targets & Responsiveness</h3>
          <ul className="space-y-2">
            {[
              'Minimum touch target size: 44×44px',
              'Provide adequate spacing between interactive elements',
              'Avoid hover-only interactions; provide tap alternatives',
              'Support pinch-to-zoom (avoid user-scalable=no)',
              'Responsive design: layout adapts to viewport',
              'Test with VoiceOver (iOS) and TalkBack (Android)',
            ].map((item) => (
              <li key={item} className="flex gap-3">
                <i className="ri-check-line ri-lg text-success-600 flex-shrink-0 mt-0.5" aria-hidden />
                <span className="text-gray-700 dark:text-gray-300">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function FinancialSection(): React.ReactElement {
  return (
    <section aria-labelledby="financial-heading">
      <h2 id="financial-heading" className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
        Financial App Specific
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-8">
        Accessibility considerations specific to financial applications.
      </p>

      <div className="space-y-8">
        {/* Overall Status, Known Issues, Recommendations */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Compliance Summary</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Track WCAG 2.2 compliance for your financial app.
          </p>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-700/50">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-success-100 dark:bg-success-900/30">
                <i className="ri-check-line ri-lg text-success-600" aria-hidden />
              </span>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Overall Status</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Partially Compliant</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-700/50">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-warning-100 dark:bg-warning-900/30">
                <i className="ri-error-warning-line ri-lg text-warning-600" aria-hidden />
              </span>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Known Issues</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">12</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-700/50">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
                <i className="ri-lightbulb-line ri-lg text-primary-600" aria-hidden />
              </span>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Recommendations</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">8</p>
              </div>
            </div>
          </div>
        </div>

        {/* Financial-specific guidelines */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Guidelines</h3>
          <ul className="space-y-4">
            {[
              'Use semantic markup for amounts (e.g. data attributes, role="text")',
              'Clearly distinguish positive vs negative amounts (colour + text)',
              'Ensure charts and graphs have text alternatives or data tables',
              'Currency symbols and decimals must be announced correctly',
              'Transaction lists should be navigable by keyboard',
              'Session timeout warnings must be accessible and dismissible',
            ].map((item) => (
              <li key={item} className="flex gap-3">
                <i className="ri-check-line ri-lg text-success-600 flex-shrink-0 mt-0.5" aria-hidden />
                <span className="text-gray-700 dark:text-gray-300">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function ChecklistSection(): React.ReactElement {
  return (
    <section aria-labelledby="checklist-heading">
      <h2 id="checklist-heading" className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
        Testing Checklist
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-8">
        Ensure your application meets accessibility standards before deployment. Follow this comprehensive checklist to
        verify compliance.
      </p>
      <div className="space-y-6">
        {CHECKLIST_CATEGORIES.map((cat) => (
          <div key={cat.title} className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h3 className="mb-4 font-medium text-gray-900 dark:text-gray-100">{cat.title}</h3>
            <ul className="space-y-2">
              {cat.items.map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <i className="ri-checkbox-blank-line ri-lg flex-shrink-0 text-gray-400" aria-hidden />
                  <span className="text-gray-700 dark:text-gray-300">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="mt-6">
        <button type="button" className="btn btn-primary btn-md" onClick={exportAccessibilityChecklistToPdf}>
          <i className="ri-file-pdf-line" aria-hidden />
          Export to PDF
        </button>
      </div>
    </section>
  );
}
